import {Box, Center, Flex, Spinner, TabPanel, TabPanels, Tabs, useTheme} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {useParams, useSearchParams, useLocation} from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {logChatMessageSend} from '../../utils/analytics';
import {isIphone} from '../../utils/deviceCheck';
import ChatHeader from './ChatHeader/PlaceChatHeader';
import ThreadTab from './ThreadTab';
import RepliesTab from './RepliesMentionsTab';
import FeaturedTab from './FeaturedTab';
import {getImage} from '../../pages/Profile/Edit/CropImage';
import {ChatMedia} from './ChatComment';
import {useFetchPlace} from '../../hooks/useFetchPlace';
import {CHAT_TABS} from './ChatHeader/ChatTabs';
import {
  useAddCommentImage,
  useCreatePlaceComment,
  useDeleteCommentImage,
  useDeletePlaceComment,
  useFetchPlaceChatUsers,
  useFetchRepliesAndMentions,
  useHelpfulPlaceComment,
  useInfinitePlaceComments,
  useInfiniteFeaturedPlaceComments,
  useReportPlaceComment,
  useUpdatePlaceComment,
} from '../../hooks/usePlaceComments';
import { TextG14 } from '../typography';
import Icon from '../Icon';

const fetchCommentsHook = useInfinitePlaceComments;

const PlaceChat = () => {
  const {placeId, tab} = useParams();
  const {user} = useAuth();
  const location = useLocation();
  const fetchPlace = useFetchPlace({id: Number(placeId)});
  const place = fetchPlace.data?.data;
  const theme = useTheme();
  let [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = CHAT_TABS.indexOf(decodeURIComponent(searchParams.get('tab') || 'Thread'));
  const [headerHeight, setHeaderHeight] = useState(0);
  const [tabIndex, setTabIndex] = useState(defaultTab == -1 ? 0 : defaultTab);
  const [hideHeader, setHideHeader] = useState(false);
  
  // Capture mention user from navigation state on mount (before setSearchParams clears it)
  const [mentionUser] = useState(() => (location.state as any)?.mentionUser);

  // Update tab when search params change (e.g., from navigation with ?tab=Thread)
  useEffect(() => {
    const newTab = CHAT_TABS.indexOf(decodeURIComponent(searchParams.get('tab') || 'Thread'));
    if (newTab !== -1 && newTab !== tabIndex) {
      setTabIndex(newTab);
    }
  }, [searchParams]);

  //const lastChatVisit = useLastChatVisit();

  const addPlaceComment = useCreatePlaceComment();
  const updatePlaceComment = useUpdatePlaceComment();
  const deletePlaceComment = useDeletePlaceComment();
  const reportPlaceComment = useReportPlaceComment();
  const helpfulPlaceComment = useHelpfulPlaceComment();
  const addCommentImage = useAddCommentImage();
  const deleteCommentImage = useDeleteCommentImage();

  const addComment = async (content: string, parent_id?: string, imageURLs?: Array<any>) => {
    if (content.length > 0) {
      const comment = await addPlaceComment.mutateAsync({
        placeId: place.id,
        content,
        parent_id,
        user: {
          id: user?.id!,
          handle: user?.handle!,
          is_walkabout_employee: user?.isWalkaboutEmployee,
          profilePhotoUrl: user?.profilePhotoUrl,
        } as any,
        hasImage: imageURLs && imageURLs.length > 0,
      });

      logChatMessageSend('place', place.id);

      if (imageURLs && imageURLs.length > 0 && comment) {
        imageURLs.map(async (media) => {
          const croppedImage = await getImage(media, null);

          if (placeId && croppedImage) {
            await addCommentImage.mutateAsync({
              formData: croppedImage,
              target_id: placeId,
              comment_id: comment.data.id,
            });
          }
        });
      }
      
      return comment;
    }
  };

  const updateComment = async (
    commentId: string,
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>,
    mediaToDelete?: ChatMedia[]
  ) => {
    if (content.length > 0) {
      await updatePlaceComment.mutateAsync({
        placeId: place.id,
        commentId,
        content,
      });

      if (imageURLs && imageURLs.length > 0) {
        imageURLs.map(async (media) => {
          const croppedImage = await getImage(media, null);
          if (placeId && croppedImage && commentId) {
            await addCommentImage.mutateAsync({
              formData: croppedImage,
              target_id: placeId,
              comment_id: commentId,
            });
          }
        });
      }

      if (mediaToDelete && mediaToDelete.length > 0) {
        mediaToDelete.map(async (media) => {
          if (media.id) {
            await deleteCommentImage.mutateAsync({mediaId: media.id});
          }
        });
      }
    }
  };

  const deleteComment = async (commentId: string) => {
    await deletePlaceComment.mutateAsync({
      placeId: place.id,
      commentId,
    });
  };

  const reportComment = async (commentId: string) => {
    await reportPlaceComment.mutateAsync({
      placeId: place.id,
      commentId,
    });
  };

  const helpfulComment = async (commentId: string) => {
    await helpfulPlaceComment.mutateAsync({
      placeId: place.id,
      commentId,
    });
  };

  // to fix issue with overscroll on iOS
  useEffect(() => {
    if (isIphone) document.body.style.overflow = 'hidden';
    return () => {
      if (isIphone) document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    setSearchParams({['tab']: CHAT_TABS[tabIndex]}, {replace: true});
  }, [tabIndex]);

  window.addEventListener('keyboardDidShow', () => {
    setHideHeader(true);
    setHeaderHeight(0);
  });

  window.addEventListener('keyboardDidHide', () => {
    setHideHeader(false);
  });

  if (!place || !user)
    return (
      <Center h="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  return (
    <Box>
      <Tabs
        outline={'none !important'}
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        isLazy
        lazyBehavior="keepMounted">
        <Flex flexDir="column">
          {!hideHeader && (
            <ChatHeader
              addComment={addComment}
              updateComment={updateComment}
              deleteComment={deleteComment}
              reportComment={reportComment}
              helpfulComment={helpfulComment}
              place={place}
              setHeaderHeight={setHeaderHeight}
              tabIndex={tabIndex}
              showTabs={true}
            />
          )}

          <TabPanels zIndex="auto" background="white">
            <TabPanel p={0}>
              <ThreadTab
                name={place.name}
                articleId={place.id}
                isRSVPd={true}
                fetchUsersHook={useFetchPlaceChatUsers}
                addComment={addComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                reportComment={reportComment}
                helpfulComment={helpfulComment}
                headerHeight={headerHeight}
                fetchCommentsHook={fetchCommentsHook}
                showUserRSVP={false}
                mentionUser={mentionUser}
                noMessageComponent={
                  <Flex flexDir="column" gap={8}>
                    <Icon iconName="fi-rr-comment" style={{fontSize: '48px', color: '#F4F4F4'}} />
                    <TextG14 fontWeight="500">
                      Welcome to this place chat just for locals!
                      <br />
                      <br />
                      Make the most out of your experience by asking questions, sharing your
                      excitement about the place, or planning meet-ups with like-minded locals on
                      Walkabout.
                    </TextG14>
                  </Flex>
                }
              />
            </TabPanel>
            <TabPanel p={0}>
              <RepliesTab
                articleId={place.id}
                placeId={place.id}
                fetchRepliesHook={useFetchRepliesAndMentions}
                headerHeight={headerHeight}
                addComment={addComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                reportComment={reportComment}
                helpfulComment={helpfulComment}
                fetchUsersHook={useFetchPlaceChatUsers}
              />
            </TabPanel>
            <TabPanel p={0}>
              <FeaturedTab
                fetchUsersHook={useFetchPlaceChatUsers}
                name={place.name}
                isRSVPd={true}
                addComment={addComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                reportComment={reportComment}
                helpfulComment={helpfulComment}
                fetchFeaturedCommentsHook={useInfiniteFeaturedPlaceComments}
                articleId={place.id}
                headerHeight={headerHeight}
              />
            </TabPanel>
          </TabPanels>
        </Flex>
      </Tabs>
    </Box>
  );
};

export default PlaceChat;
